import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Loader2, Printer } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import type { Parcel } from "../../types/Dispatch";
import { detectPrintMode, getSavedNetworkPrinter, submitNetworkPrintJob } from "../helpers/networkPrint";
import { browserPrint, getSavedLocalPrinter, silentPrint } from "../helpers/printAgent";
import {
    base64ToZpl,
    downloadPdfBase64,
    printPdfBase64,
    printPdfsBase64,
    printZplWithCascade,
    type PrintMode,
    type PrintResult,
} from "../helpers/carrierLabelPrint";
import { logDispatchEvent } from "./dispatchLog";

// Reprint / download a shipped parcel's label from the Orders page.
//
// The BE re-serves the label the pack desk printed — stored booking ZPL
// for APC / InPost, the uploaded shipping_label attachment for own-label
// shipments — via POST /orders/:id/parcels/:tracking/label. Printing takes
// the same cascade as the original (network Zebra → local agent → PDF in
// a hidden iframe), so a reprint comes out on the same stock with the
// same "box n of N" stamp. Download always asks for PDF.
//
// COLLECTION parcels (carrier 'Collection', delivery_type = collection) are
// the exception: nothing is stored for them, the pack desk built the label
// in the browser from the order itself. The Orders page renders the same
// <CollectionLabel> into a hidden area and hands its HTML in through
// `collectionLabelHtml`, and the reprint takes the desk's HTML cascade
// (network Zebra → local agent → browser print). No download: there is
// no PDF to serve.

export const COLLECTION_CARRIER = "Collection";

export const isCollectionParcel = (p: Pick<Parcel, "carrier">): boolean =>
    String(p.carrier ?? "").trim().toLowerCase() === COLLECTION_CARRIER.toLowerCase();

export type CollectionLabelHtmlProvider = (p: Parcel) => Promise<string | null> | string | null;

export type ParcelLabelFormat = "ZPL" | "PDF";

export type ParcelLabelResponse = {
    status: "label_ready";
    format: ParcelLabelFormat;
    tracking_number: string;
    content_base64: string;
    carrier?: string | null;
    /** Own-label parcels: the attachment's own filename. */
    filename?: string;
};

export const fetchParcelLabel = async (
    orderId: string,
    trackingNumber: string,
    format: ParcelLabelFormat,
    fallbackError: string,
): Promise<ParcelLabelResponse> => {
    const res = await apiFetch(
        `/orders/${encodeURIComponent(orderId)}/parcels/${encodeURIComponent(trackingNumber)}/label`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ format }),
        },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status !== "label_ready" || !data.content_base64) {
        throw new Error(data.error ?? data.message ?? fallbackError);
    }
    return data as ParcelLabelResponse;
};

export const parcelLabelFilename = (orderId: string, p: Parcel, idx: number, fromServer?: string) =>
    fromServer || `${orderId}-parcel-${p.box_position ?? idx + 1}-label.pdf`;

/** Parcels the table can offer a label for, in shipment order. A
 *  collection parcel is always printable: its label is rebuilt from the
 *  order, not fetched. */
export const printableParcels = (parcels: Parcel[] | undefined): Parcel[] =>
    (parcels ?? [])
        .filter(p => p.label_available || isCollectionParcel(p))
        .slice()
        .sort((a, b) => (a.box_position ?? 0) - (b.box_position ?? 0));

// The pack desk's HTML print cascade for a label built in the browser:
// the chosen network Zebra, else the local print agent (honouring the
// printer picked in the modal), else the browser's own print dialog.
const printHtmlWithCascade = async (html: string, mode: PrintMode): Promise<PrintResult> => {
    let result: PrintResult = { success: false };
    const saved = getSavedNetworkPrinter();
    if (mode === "network" && saved) {
        result = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1);
    }
    if (!result.success) {
        result = await silentPrint(html, 1, getSavedLocalPrinter() ?? undefined);
    }
    if (!result.success) {
        result = await browserPrint(html);
    }
    return result;
};

export type UseParcelLabelsOptions = {
    /** Builds the print document for a collection parcel; null when the
     *  label area has not rendered (the print then fails loudly). */
    collectionLabelHtml?: CollectionLabelHtmlProvider;
};

export const useParcelLabels = (
    orderId: string | null | undefined,
    parcels: Parcel[] | undefined,
    options: UseParcelLabelsOptions = {},
) => {
    const { collectionLabelHtml } = options;
    const { t } = useTranslation();
    // Tracking number of the parcel in flight, or "*" while Print all runs.
    const [busy, setBusy] = useState<string | null>(null);
    // Same shape as the pack desk: detection runs once on mount and is
    // re-run when the printer pill reports a change, so the print call
    // never targets a printer the operator just moved away from.
    const printModeRef = useRef<Promise<PrintMode> | null>(null);
    const detectPrinter = useCallback(() => {
        const detection = detectPrintMode();
        printModeRef.current = detection;
        return detection;
    }, []);
    useEffect(() => { detectPrinter(); }, [detectPrinter]);

    // pdfQueue: Print all collects PDF labels here instead of printing each
    // one, then prints them through ONE dialog — back-to-back print()
    // calls lose every label after the first (see printPdfsBase64).
    const printOne = useCallback(async (p: Parcel, pdfQueue?: string[]): Promise<PrintResult> => {
        if (!orderId) return { success: false };
        if (isCollectionParcel(p)) {
            const html = collectionLabelHtml ? await collectionLabelHtml(p) : null;
            if (!html) return { success: false, error: t("label_fetch_failed") };
            const mode = await (printModeRef.current ?? detectPrinter());
            return printHtmlWithCascade(html, mode);
        }
        const label = await fetchParcelLabel(orderId, p.tracking_number, "ZPL", t("label_fetch_failed"));
        if (label.format !== "ZPL") {
            // Own-label parcels come back as the attachment PDF.
            if (pdfQueue) { pdfQueue.push(label.content_base64); return { success: true }; }
            const ok = await printPdfBase64(label.content_base64);
            return ok ? { success: true } : { success: false, error: t("print_failed") };
        }
        const mode = await (printModeRef.current ?? detectPrinter());
        const zplResult = await printZplWithCascade(base64ToZpl(label.content_base64), mode);
        if (zplResult.success) return zplResult;
        // No Zebra route (no agent / non-Zebra desk) → PDF fallback, the
        // same as the pack desk. The PDF is fetched from the carrier on
        // demand (never stored), so when THAT fails the printer error is
        // still the headline — the carrier detail is appended so a bare
        // "password not configured" never reads as the whole story.
        const printerError = zplResult.error ?? t("print_agent_not_found");
        let pdf: ParcelLabelResponse;
        try {
            pdf = await fetchParcelLabel(orderId, p.tracking_number, "PDF", t("label_fetch_failed"));
        } catch (err) {
            const detail = err instanceof Error ? err.message : t("label_fetch_failed");
            return { success: false, error: `${printerError} — ${t("label_pdf_fallback_failed")}: ${detail}` };
        }
        if (pdfQueue) { pdfQueue.push(pdf.content_base64); return { success: true }; }
        const ok = await printPdfBase64(pdf.content_base64);
        return ok ? { success: true } : { success: false, error: printerError };
    }, [orderId, detectPrinter, collectionLabelHtml, t]);

    const printParcel = useCallback(async (p: Parcel) => {
        if (busy || !orderId) return;
        setBusy(p.tracking_number);
        let result: PrintResult;
        try {
            result = await printOne(p);
        } catch (err) {
            result = { success: false, error: err instanceof Error ? err.message : t("print_failed") };
        }
        logDispatchEvent({
            event: "label-reprint",
            order_id: orderId,
            carrier: p.carrier,
            tracking_number: p.tracking_number,
            result: result.success ? "ok" : `fail-${result.error ?? "print"}`.slice(0, 80),
        });
        if (result.success) toast.success(t("label_reprinted"), { toastId: "label-reprint-ok" });
        else toast.error(result.error || t("print_failed"), { toastId: "label-reprint-err" });
        setBusy(null);
    }, [busy, orderId, printOne, t]);

    const downloadParcel = useCallback(async (p: Parcel, idx: number) => {
        if (busy || !orderId) return;
        setBusy(p.tracking_number);
        try {
            const pdf = await fetchParcelLabel(orderId, p.tracking_number, "PDF", t("label_fetch_failed"));
            downloadPdfBase64(pdf.content_base64, parcelLabelFilename(orderId, p, idx, pdf.filename));
        } catch (err) {
            toast.error(err instanceof Error ? err.message : t("label_fetch_failed"), { toastId: "label-dl-err" });
        } finally {
            setBusy(null);
        }
    }, [busy, orderId, t]);

    const printAll = useCallback(async () => {
        if (busy || !orderId) return;
        const list = printableParcels(parcels);
        if (list.length === 0) return;
        setBusy("*");
        let printed = 0;
        let lastError: string | undefined;
        const pdfQueue: string[] = [];
        for (const p of list) {
            let r: PrintResult;
            try {
                r = await printOne(p, pdfQueue);
            } catch (err) {
                r = { success: false, error: err instanceof Error ? err.message : t("print_failed") };
            }
            logDispatchEvent({
                event: "label-reprint",
                order_id: orderId,
                carrier: p.carrier,
                tracking_number: p.tracking_number,
                result: r.success ? "ok" : `fail-${r.error ?? "print"}`.slice(0, 80),
            });
            // Stop at the first failure so a dead printer doesn't queue
            // every remaining label for whenever it comes back.
            if (!r.success) { lastError = r.error; break; }
            printed += 1;
        }
        if (pdfQueue.length > 0 && !(await printPdfsBase64(pdfQueue))) {
            printed -= pdfQueue.length;
            lastError = t("print_failed");
        }
        if (printed === list.length) {
            toast.success(t("printed_n_labels", { count: printed }), { toastId: "label-reprint-ok" });
        } else {
            toast.error(
                `${t("print_partial_failed", { failed: list.length - printed, total: list.length })}${lastError ? ` — ${lastError}` : ""}`,
                { toastId: "label-reprint-err" },
            );
        }
        setBusy(null);
    }, [busy, orderId, parcels, printOne, t]);

    return { busy, printParcel, downloadParcel, printAll, detectPrinter };
};

type ButtonsProps = {
    parcel: Parcel;
    /** Row index — the filename fallback when the parcel has no position. */
    index: number;
    busy: string | null;
    onPrint: (p: Parcel) => void;
    onDownload: (p: Parcel, idx: number) => void;
};

/** The two icon buttons in the Shipment table's Label column. Renders a
 *  dash for parcels with nothing to reprint (manual / marketplace
 *  fulfilments, cancelled bookings). A collection parcel gets Print only —
 *  the label is rebuilt in the browser, there is no PDF to download. */
export const ParcelLabelButtons = ({ parcel, index, busy, onPrint, onDownload }: ButtonsProps) => {
    const { t } = useTranslation();
    const collection = isCollectionParcel(parcel);
    if (!parcel.label_available && !collection) return <>—</>;
    const mine = busy === parcel.tracking_number;
    const disabled = busy !== null;
    if (collection) {
        return (
            <span className="dispatch-parcel-label-actions">
                <button
                    type="button"
                    className="dispatch-parcel-label-btn"
                    onClick={() => onPrint(parcel)}
                    disabled={disabled}
                    title={t("print_label")}
                    aria-label={t("print_label")}
                >
                    {mine ? <Loader2 size={14} className="spin" /> : <Printer size={14} />}
                </button>
            </span>
        );
    }
    return (
        <span className="dispatch-parcel-label-actions">
            <button
                type="button"
                className="dispatch-parcel-label-btn"
                onClick={() => onPrint(parcel)}
                disabled={disabled}
                title={t("print_label")}
                aria-label={t("print_label")}
            >
                {mine ? <Loader2 size={14} className="spin" /> : <Printer size={14} />}
            </button>
            <button
                type="button"
                className="dispatch-parcel-label-btn"
                onClick={() => onDownload(parcel, index)}
                disabled={disabled}
                title={t("download_label_pdf")}
                aria-label={t("download_label_pdf")}
            >
                <Download size={14} />
            </button>
        </span>
    );
};

export default ParcelLabelButtons;
